import { createContext, useCallback, useContext, useEffect, useMemo, type CSSProperties, type ReactNode } from 'react';
import type { Characteristic, Component, Value } from '../schema';
import { formatValuePrice } from '../pricing/format';
import { toHostAmount } from '../pricing/host';
import { effectiveValuePrice } from '../pricing/choiceMatrix';
import { usePriceBreakdown } from '../pricing/PriceContext';
import { useEvaluatedMap } from '../eval/EvalContext';
import { useTranslation } from '../i18n';

/**
 * Exposes the parsed Component as a Context so deeply-nested views can
 * read Component-level settings (displayPrice, valueImagePosition, …)
 * without each render-call dragging the whole tree through prop drilling.
 *
 * Defaults mirror a "sensible storefront" — `displayPrice: true`,
 * `valueImagePosition: 'left'`, EUR. They only apply when a view renders
 * outside any Provider (legacy snapshot tests, etc.).
 */
export interface ComponentSettings {
    displayPrice: boolean;
    displayPriceIfZero: boolean;
    displayPriceType: 'absolute' | 'relative';
    displayPricePattern: string | undefined;
    priceIncludesTax: boolean;
    valueImagePosition: 'left' | 'right' | 'donotshow' | string;
    showMinMax: boolean;
    minMaxPattern: string | undefined;
}

const DEFAULTS: ComponentSettings = Object.freeze({
    displayPrice: true,
    displayPriceIfZero: false,
    displayPriceType: 'absolute',
    displayPricePattern: undefined,
    priceIncludesTax: true,
    valueImagePosition: 'left',
    showMinMax: false,
    minMaxPattern: undefined,
});

const ComponentContext = createContext<ComponentSettings>(DEFAULTS);
const ComponentObjectContext = createContext<Component | null>(null);

export function ComponentProvider({
    component,
    children,
}: {
    component: Component;
    children: ReactNode;
}) {
    const value = useMemo<ComponentSettings>(
        () => ({
            displayPrice: component.displayPrice,
            displayPriceIfZero: component.displayPriceIfZero,
            displayPriceType: component.displayPriceType,
            displayPricePattern: component.displayPricePattern,
            priceIncludesTax: component.priceIncludesTax,
            valueImagePosition: component.valueImagePosition,
            showMinMax: component.showMinMax,
            minMaxPattern: component.minMaxPattern,
        }),
        [
            component.displayPrice,
            component.displayPriceIfZero,
            component.displayPriceType,
            component.displayPricePattern,
            component.priceIncludesTax,
            component.valueImagePosition,
            component.showMinMax,
            component.minMaxPattern,
        ],
    );
    return (
        <ComponentObjectContext.Provider value={component}>
            <ComponentContext.Provider value={value}>{children}</ComponentContext.Provider>
        </ComponentObjectContext.Provider>
    );
}

/**
 * Inline custom properties for the admin's colour settings. Precedence of
 * the accent:
 *  1. the component's active colour (Komponenten-Einstellungen → Design →
 *     Aktivfarbe) — set straight on `--cfg-color-accent`, so it beats
 *     everything, also `--configento-color-accent` from the host;
 *  2. `--configento-color-accent` from the host CSS;
 *  3. the branding colour (Einstellungen → Branding → Primärfarbe) — only
 *     fills `--cfg-brand-accent`, the fallback inside the token block;
 *  4. the bundle default.
 * Applied on `.cfg-root` and again on every overlay that portals out of it,
 * which would otherwise miss it.
 */
export function themeStyle(component: Pick<Component, 'activeColor' | 'brandColor'> | null): CSSProperties | undefined {
    const style: Record<string, string> = {};
    if (component?.brandColor) style['--cfg-brand-accent'] = component.brandColor;
    if (component?.activeColor) style['--cfg-color-accent'] = component.activeColor;
    return Object.keys(style).length > 0 ? (style as CSSProperties) : undefined;
}

/** {@link themeStyle} for the Component in context; undefined outside a provider. */
export function useThemeStyle(): CSSProperties | undefined {
    const component = useContext(ComponentObjectContext);
    const activeColor = component?.activeColor;
    const brandColor = component?.brandColor;
    return useMemo(() => themeStyle({ activeColor, brandColor }), [activeColor, brandColor]);
}

/**
 * {@link useThemeStyle} for a container the bundle creates itself and hands
 * to a host modal adapter, which may re-parent it anywhere in the page.
 */
export function useThemeStyleOn(el: HTMLElement | null): void {
    const style = useThemeStyle();
    useEffect(() => {
        if (!el || !style) return;
        const entries = Object.entries(style) as Array<[string, string]>;
        for (const [name, value] of entries) el.style.setProperty(name, value);
        return () => {
            for (const [name] of entries) el.style.removeProperty(name);
        };
    }, [el, style]);
}

export function useComponentSettings(): ComponentSettings {
    return useContext(ComponentContext);
}

/**
 * Full parsed Component. Throws if used outside a {@link ComponentProvider} —
 * callers that may render before the provider mounts (state-card placeholders)
 * use {@link useOptionalComponent} instead.
 */
export function useComponent(): Component {
    const c = useContext(ComponentObjectContext);
    if (c === null) {
        throw new Error('useComponent must be called inside ComponentProvider');
    }
    return c;
}

/** Convenience selector — most views only care about this flag. */
export function useDisplayPrice(): boolean {
    return useContext(ComponentContext).displayPrice;
}

/**
 * Returns a function that resolves the effective price of a Value within
 * a given Characteristic. Honours the Characteristic's `choicePriceMatrix`
 * lookup when configured ("Preise für Wert aus Matrix") — otherwise
 * falls back to the static `value.price`.
 *
 * Used by views to compute the per-Value relative-mode price suffix; the
 * pricing engine itself uses the same helper directly on the pricing pass.
 */
export function useEffectiveValuePrice(): (value: Value, characteristic: Characteristic) => number {
    const component = useContext(ComponentObjectContext);
    const evaluated = useEvaluatedMap();
    return useCallback(
        (value, characteristic) => {
            if (!component) return parsePriceFallback(value.price);
            return effectiveValuePrice(value, characteristic, component, evaluated);
        },
        [component, evaluated],
    );
}

function parsePriceFallback(raw: string | undefined): number {
    if (raw == null || raw === '') return 0;
    const cleaned = raw.endsWith('P') ? raw.slice(0, -1) : raw;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
}

/**
 * Converts a configurator amount into the shop's currency and tax basis —
 * the same conversion the total and every price suffix get.
 */
export function useHostPriceConverter(): (amount: number) => number {
    const component = useContext(ComponentObjectContext);
    const { currency, currencyRate, storeTaxRate } = usePriceBreakdown();
    return useCallback(
        (amount: number) =>
            component
                ? toHostAmount(amount, component, { storeTaxRate }, { currency, rate: currencyRate })
                : amount,
        [component, storeTaxRate, currency, currencyRate],
    );
}

/**
 * Returns a formatter for a Value-level price suffix that honours the
 * Component's price-display settings (`displayPrice`, `displayPriceIfZero`,
 * `displayPricePattern`, `displayPriceType`) and the active currency/locale.
 *
 * The returned function takes an optional `relativeBase` — only relevant in
 * `displayPriceType: 'relative'` mode where the suffix is computed as the
 * delta against the currently picked Value's price within the same
 * Characteristic. Callers that don't track a base can omit it.
 *
 * Empty string return means "render nothing".
 */
export function useValuePriceFormatter(): (
    price: string | number | undefined | null,
    relativeBase?: string | number | undefined | null,
) => string {
    const settings = useContext(ComponentContext);
    const component = useContext(ComponentObjectContext);
    const { currency, currencyRate, storeTaxRate } = usePriceBreakdown();
    const { locale } = useTranslation();
    // Same conversion the total gets: a suffix in the configurator's own
    // basis next to a total in the shop's would not add up.
    const convert = useMemo(
        () =>
            component
                ? (amount: number) =>
                      toHostAmount(amount, component, { storeTaxRate }, { currency, rate: currencyRate })
                : undefined,
        [component, storeTaxRate, currency, currencyRate],
    );
    return useCallback(
        (price, relativeBase) =>
            formatValuePrice(price, {
                displayPrice: settings.displayPrice,
                displayPriceIfZero: settings.displayPriceIfZero,
                displayPricePattern: settings.displayPricePattern,
                displayPriceType: settings.displayPriceType,
                relativeBasePrice: relativeBase ?? undefined,
                currency,
                locale,
                convert,
            }),
        [
            settings.displayPrice,
            settings.displayPriceIfZero,
            settings.displayPricePattern,
            settings.displayPriceType,
            currency,
            locale,
            convert,
        ],
    );
}
